import { useEffect, useState } from "react";
import { ShoppingCart, Download, HardDrive, BookOpen, Tablet, Smartphone, Laptop } from "lucide-react";
import { getBooks } from "@/lib/api";
import BookCard from "@/components/BookCard";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";

const STEPS = [
  { icon: ShoppingCart, title: "Compra segura", text: "Elige tu ebook y paga con tarjeta, Apple Pay o Google Pay en la pasarela segura de Stripe, sin crear ninguna cuenta." },
  { icon: Download, title: "Descarga inmediata", text: "Al terminar el pago descargas el archivo EPUB al momento. También te enviamos por email el enlace de descarga y tu factura." },
  { icon: HardDrive, title: "Es tuyo para siempre", text: "Una vez descargado, guárdalo donde quieras: tu ordenador, un disco duro, una memoria USB o la nube. Sin suscripciones." },
  { icon: BookOpen, title: "Léelo donde prefieras", text: "Ábrelo en tu lector o aplicación de lectura favorita y disfruta del libro a tu ritmo." },
];

const READERS = [
  { icon: Smartphone, title: "Móvil y tablet", text: "Apple Books (iPhone/iPad), Google Play Libros o la app de Kobo: abre el archivo EPUB o súbelo desde la app." },
  { icon: Tablet, title: "Lectores de tinta electrónica", text: "Kobo, PocketBook y la mayoría de lectores leen EPUB: conéctalo al ordenador y copia el archivo. En Kindle, usa «Enviar a Kindle» de Amazon, que acepta EPUB." },
  { icon: Laptop, title: "Ordenador", text: "En Mac, con Apple Books. En Windows o Linux, con lectores gratuitos como Calibre o Thorium Reader." },
];

const Card = ({ icon: Icon, title, text, i }) => (
  <Reveal delay={i * 0.06}>
    <div className="h-full rounded-[24px] bg-white border border-[#0f2544]/8 p-6 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(15,37,68,0.08)]">
      <span className="grid place-items-center w-11 h-11 rounded-full bg-[#1f44e0]/10 text-[#1f44e0]"><Icon size={19} /></span>
      <h3 className="font-serif text-xl font-semibold text-[#0f2544] mt-5">{title}</h3>
      <p className="text-sm text-[#4a4a45] leading-relaxed mt-2">{text}</p>
    </div>
  </Reveal>
);

export default function Ebooks() {
  const [books, setBooks] = useState(null);
  useEffect(() => { getBooks({ ebook: true }).then(setBooks).catch(() => setBooks([])); }, []);

  return (
    <>
      <Seo title="Ebooks" description="Compra los ebooks de Editorial Harvest en formato EPUB, descárgalos al instante y léelos en tu dispositivo favorito." />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-14 pb-10">
        <p className="hv-overline">Venta directa · Formato EPUB</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#0f2544] mt-3" data-testid="ebooks-title">Ebooks</h1>
        <p className="text-[#4a4a45] mt-4 max-w-2xl font-medium">
          Compra nuestros libros en formato digital, descárgalos al instante y llévalos contigo en tu ordenador, tablet, móvil o lector de libros electrónicos.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-16" data-testid="ebooks-steps">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((s, i) => <Card key={s.title} {...s} i={i} />)}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-20" data-testid="ebooks-list">
        <div className="flex items-baseline gap-4 border-t border-[#0f2544]/15 pt-6 mb-10">
          <span className="hv-overline">01</span>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight text-[#0f2544]">Ebooks disponibles</h2>
        </div>
        {books === null ? (
          <p className="text-[#8a8a83]">Cargando...</p>
        ) : books.length === 0 ? (
          <p className="text-[#8a8a83] rounded-2xl border border-dashed border-[#0f2544]/15 bg-white py-12 text-center" data-testid="ebooks-empty">Muy pronto tendrás aquí nuestros ebooks.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
            {books.map((b, i) => <Reveal key={b.slug} delay={(i % 5) * 0.05}><BookCard book={b} testId={`ebook-card-${b.slug}`} /></Reveal>)}
          </div>
        )}
      </section>

      <section className="bg-[#0f2544]" data-testid="ebooks-readers">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 md:py-20">
          <p className="hv-overline !text-[#e6c364]">¿Qué es un EPUB?</p>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mt-3 max-w-2xl">El formato estándar de los libros electrónicos</h2>
          <p className="text-white/75 mt-4 max-w-2xl leading-relaxed">
            El EPUB es un archivo que se adapta al tamaño de tu pantalla y te permite cambiar la letra, el tamaño del texto o el fondo. Una vez descargado, puedes guardarlo y abrirlo en casi cualquier dispositivo:
          </p>
          <div className="grid md:grid-cols-3 gap-4 mt-10">
            {READERS.map((r) => (
              <div key={r.title} className="rounded-[24px] bg-white/5 border border-white/10 p-6">
                <r.icon size={20} className="text-[#e6c364]" />
                <h3 className="font-serif text-lg font-semibold text-white mt-4">{r.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed mt-2">{r.text}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-white/50 mt-8">Tu enlace de compra es personal: permite 2 descargas (por ejemplo, móvil y tablet o lector de ebooks) durante 15 días. Si necesitas ayuda, escríbenos desde la página de contacto.</p>
        </div>
      </section>
    </>
  );
}
